import type { Sprite } from 'pixi.js';
import { Container, Graphics, Color } from 'pixi.js';
import type { BlockerView } from '../../types';
import type { ScoreBlockerModel } from '../../models/blockers/ScoreBlockerModel';
import { ScoreBlockerLayout } from '../../../../../layouts/blockers/ScoreBlockerLayout';
import { shuffle } from '../../../../utils/array-utils';
import { waitForMs } from '../../../../utils/async-utils';
import type { ParticleInitConfig } from '../../../../libs/particles/updateParticle';
import { turnIntoParticle } from '../../../../libs/particles/updateParticle';
import { FXParticleLayer } from '../../../../libs/particles/FXParticleLayer';
import type { FXParticleEmitterConfig } from '../../../../libs/particles/FXParticleEmitterConfig';
import { soundManager } from '../../../../app/SoundManager';
import emitterJSON from '../../../../../assets/particles{ignore}/Score_Blocker_Unlock_Particles.json';

const aboutToCompleteScoreRange = 20;

export class ScoreBlockerStackView extends Container implements BlockerView {
  private readonly layout = new ScoreBlockerLayout();

  private readonly particleLayer = new FXParticleLayer(true);

  constructor(private readonly model: ScoreBlockerModel) {
    super();

    this.init();

    const onProgressChanged = (current: number, target: number): void => {
      model.onProgressChanged.disconnect(onProgressChanged);
      if (target - current <= aboutToCompleteScoreRange) {
        this.startAttractLoop();
      }
    };
    model.onProgressChanged.connect(onProgressChanged);

    this.setScore(model.getScore());
  }

  public getModel(): ScoreBlockerModel {
    return this.model;
  }

  public setScore(score: number): void {
    this.layout.scoreText.text = score;
  }

  private init(): void {
    const { root, shatteredContainer } = this.layout;
    shatteredContainer.visible = false;
    this.addChild(this.particleLayer, root);
    this.initIceHighlightAnimation();
  }

  private initIceHighlightAnimation(): Graphics {
    const { iceLayerHighlight } = this.layout;

    const gfx = new Graphics().rect(-20, -100, 40, 200).fill(new Color(0xffffff)).props({
      angle: -35,
      x: -100,
    });

    iceLayerHighlight.props({
      mask: gfx,
      alpha: 0.2,
    });

    gfx.tweenTo({ x: 100 }, 650, 'linear').repeatDelay(4000).repeat(-1).start();

    return gfx;
  }

  private startAttractLoop(): void {
    if (this.destroyed) return;

    const loopDelay = 2000;
    const initialAngleOffset = 3;
    const bounces = 3;
    let bouncesRemaining = bounces;
    const elasticity = 0.6;
    const tweenDuration = 250;
    const { lockContainer, scoreText } = this.layout;

    let toAngle = initialAngleOffset;

    const createLoopTweens = (): void => {
      const delay = bouncesRemaining === bounces ? loopDelay : 0;

      if (bouncesRemaining === bounces) {
        scoreText.removeTweens();
        scoreText.tweenTo({ scaleX: 1.1, scaleY: 1.1 }, (tweenDuration * bounces) / 2, 'sinIn', true, delay, 0, true);
      }

      lockContainer.removeTweens();
      lockContainer.tweenTo({ angle: toAngle }, tweenDuration, 'sinOut', true, delay).onComplete.addOnce(() => {
        lockContainer.tweenTo({ angle: 0 }, tweenDuration, 'sinOut', true).onComplete.addOnce(() => {
          const bouncesComplete = bouncesRemaining === 0;
          toAngle = !bouncesComplete ? toAngle * -elasticity : initialAngleOffset;
          bouncesRemaining += bouncesComplete ? bounces : -1;
          // FIXME: investigate this was activating sometimes even though the Tweens were stopped.
          if (!this.model.isUnlocked()) createLoopTweens();
        });
      });
    };
    createLoopTweens();
  }

  public async animateRemoval(): Promise<void> {
    if (this.destroyed) return;

    const { particleLayer } = this;
    const { lockContainer, iceLayerHighlight, scoreText } = this.layout;

    scoreText.removeTweens();
    lockContainer.removeTweens();

    if (lockContainer.angle !== 0) await lockContainer.tweenTo({ angle: 0 }, 100, 'quadOut', true).asyncOnComplete();

    await this.animateHideScore();

    iceLayerHighlight.tweenTo({ alpha: 0 }, 100, 'quadIn', true);
    particleLayer.addParticleEmitter(0, 15, { ...emitterJSON, startDelay: 0 } as FXParticleEmitterConfig);
    await waitForMs(100);

    soundManager.playDelayed(`Gameplay_Unlock_Tile`, 100);
    this.animateTileHighlight();
    this.animateOpenLock();
    this.animateLockHighlight(200);
    await waitForMs(500);

    void this.animateShards();
    this.animateHideLock();
    await waitForMs(400);

    this.hideTileHighlight();
    await waitForMs(500);
  }

  private animateLockHighlight(pauseDuration: number): void {
    if (this.destroyed) return;

    const { lockBodyHighlight, lockHookHighlight } = this.layout;
    [lockBodyHighlight, lockHookHighlight].forEach(async (elem: Sprite) => {
      elem.tweenTo({ alpha: 0.8 }, 300, 'sinOut', true);
      await waitForMs(pauseDuration);
      elem.tweenTo({ alpha: 0 }, 300, 'sinOut', true);
    });
  }

  private animateTileHighlight(): void {
    if (this.destroyed) return;
    const { tileHighlight } = this.layout;
    tileHighlight.tweenTo({ alpha: 1 }, 500, 'sinOut', true);
  }

  private hideTileHighlight(): void {
    if (this.destroyed) return;
    const { tileHighlight } = this.layout;
    tileHighlight.tweenTo({ alpha: 0 }, 300, 'sinOut', true);
  }

  private async animateHideScore(): Promise<void> {
    if (this.destroyed) return;

    this.layout.scoreText.removeTweens();
    await this.layout.scoreText
      .tweenTo(
        {
          scaleX: 0,
          scaleY: 0,
        },
        500,
        'elasticIn',
        true,
      )
      .asyncOnComplete();
  }

  private animateOpenLock(): void {
    if (this.destroyed) return;
    const { lockHook, lockContainer } = this.layout;
    lockContainer.tweenTo({ scaleX: 0.9, scaleY: 0.9 }, 300, 'sinOut', true);
    lockHook.tweenTo({ y: lockHook.y - 15 }, 500, 'elasticOut', true);
  }

  private animateHideLock(): void {
    if (this.destroyed) return;
    const { lockContainer } = this.layout;
    lockContainer.tweenTo({ scaleX: 0.25, scaleY: 0.25 }, 300, 'backIn', true, 0);
    lockContainer.tweenTo({ alpha: 0 }, 50, 'quadOut', true, 250);
  }

  private async animateShards(): Promise<void> {
    if (this.destroyed) return;

    const { shatteredContainer, iceLayer } = this.layout;

    iceLayer.visible = false;
    shatteredContainer.visible = true;

    const shards = shuffle(shatteredContainer.children.slice());

    const commonConfig: Omit<ParticleInitConfig, 'angularVelocity'> = {
      velocity: {
        distance: [0.05, 0.2],
        angle: [Math.PI * 0.5, Math.PI * -0.5],
        drag: 0.95,
      },
      gravity: 0.0005,
      angularDrag: 1,
    };

    await Promise.all(
      shards.map(async (shard, index): Promise<void> => {
        if (index > 2) {
          await waitForMs(50);
        }

        if (this.destroyed) return Promise.resolve();

        turnIntoParticle(shard as Sprite, {
          ...commonConfig,
          velocity: {
            ...commonConfig.velocity,
            angle: Math.atan2(shard.y, shard.x),
          },
          angularVelocity: shard.x * 0.001,
        });

        return shard
          .tweenTo(
            {
              alpha: 0,
            },
            200,
            'sinOut',
            true,
            100,
          )
          .asyncOnComplete();
      }),
    );
  }

  public destroy(): void {
    this.particleLayer.destroy();
    super.destroy({ children: true });
  }
}
